/**
 * DrawerContext — web port of RN's drawer/overlay model.
 *
 * Supports a single open named drawer at a time (mirrors RN Dashboard's pattern
 * of toggling one of `showItinerary` / `showSettings` / `showDeals` etc. via
 * boolean state). The bottom nav opens these by name — they are NOT routes.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from 'react';

export type DrawerId =
  | 'itinerary'
  | 'explore'
  | 'book'
  | 'deals'
  | 'chat'
  | 'inbox'
  | 'settings'
  | 'wallet'
  | 'language'
  | 'search'
  | 'companion'
  | 'interpreter'
  | 'agentSwitcher'
  | 'guestSignup';

interface DrawerContextType {
  /** Which named drawer is currently open (null = none). */
  currentDrawer: DrawerId | null;
  /** Open the named drawer (closes any other). */
  openDrawer: (id: DrawerId) => void;
  /** Close the open drawer. */
  closeDrawer: () => void;
  /** Toggle a named drawer. */
  toggleDrawer: (id: DrawerId) => void;
  /** Convenience: is this drawer currently open. */
  isOpen: (id: DrawerId) => boolean;

  /** Trip wizard sheet (modal, not a drawer). */
  tripWizardOpen: boolean;
  openTripWizard: () => void;
  closeTripWizard: () => void;

  /** Legacy boolean (preserved for back-compat with existing components). */
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [currentDrawer, setCurrentDrawer] = useState<DrawerId | null>(null);
  const [tripWizardOpen, setTripWizardOpen] = useState(false);

  const openDrawer = useCallback((id: DrawerId) => setCurrentDrawer(id), []);
  const closeDrawer = useCallback(() => setCurrentDrawer(null), []);
  const toggleDrawer = useCallback(
    (id: DrawerId) => setCurrentDrawer(prev => (prev === id ? null : id)),
    [],
  );
  const isOpen = useCallback(
    (id: DrawerId) => currentDrawer === id,
    [currentDrawer],
  );

  const openTripWizard = useCallback(() => setTripWizardOpen(true), []);
  const closeTripWizard = useCallback(() => setTripWizardOpen(false), []);

  const setIsDrawerOpen = useCallback(
    (open: boolean) => setCurrentDrawer(open ? 'settings' : null),
    [],
  );

  return (
    <DrawerContext.Provider
      value={{
        currentDrawer,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        isOpen,
        tripWizardOpen,
        openTripWizard,
        closeTripWizard,
        isDrawerOpen: currentDrawer !== null,
        setIsDrawerOpen,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  const ctx = useContext(DrawerContext);
  if (!ctx) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return ctx;
};
